import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, ArrowDown, Star, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lines = ["Impianti dentali", "a carico immediato:", "torna a sorridere."];

const MaskedLine = ({ text, i, italicWord }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {italicWord ? (
        <>
          {text.replace(italicWord, "")}
          <em className="italic text-[#385244]">{italicWord}</em>
        </>
      ) : (
        text
      )}
    </motion.span>
  </span>
);

const shorten = (t, max = 150) => (t.length > max ? t.slice(0, t.lastIndexOf(" ", max)) + "…" : t);


export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [google, setGoogle] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(({ data }) => setGoogle(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const n = google?.reviews?.length || 0;
    if (n < 2) return;
    const id = setInterval(() => setReviewIdx((i) => (i + 1) % n), 6000);
    return () => clearInterval(id);
  }, [google]);

  const review = google?.reviews?.[reviewIdx];

  return (
    <section ref={ref} id="top" className="relative pt-header pb-16 md:pb-24 px-6 md:px-12" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-8 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-6"
            data-testid="hero-eyebrow"
          >
            Implantologia dentale — 8 studi dentistici convenzionati in provincia di Milano e di Monza Brianza, Novara, Chivasso (TO), Seriate (Bergamo) e Palermo
          </motion.p>
          <h1 className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.08] text-[#15201A]" data-testid="hero-title">
            {lines.map((l, i) => (
              <MaskedLine key={l} text={l} i={i} italicWord={i === 2 ? "sorridere." : null} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 text-lg md:text-xl leading-relaxed text-[#5C6B61] max-w-xl"
            data-testid="hero-subtitle"
          >
            <strong className="text-lg md:text-xl font-extrabold text-[#15201A]">Denti fissi in 6 ore</strong>, se le condizioni cliniche lo consentono: avrai il provvisorio
            fisso in giornata, al posto della dentiera. Quanto costano gli impianti dentali? Prezzo chiaro e trasparente:{" "}
            <strong className="text-[#C98E6E] font-extrabold">€4.900 ad arcata</strong>, tutto incluso, con garanzia economica 10 anni.
            Prima visita con TAC in sede in uno dei nostri 8 studi convenzionati.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href="#contatti"
              className="inline-flex items-center rounded-full bg-[#385244] hover:bg-[#2B4035] text-white text-base font-bold px-9 py-4 transition-colors duration-300 active:scale-95"
              data-testid="hero-cta-btn"
            >
              Richiedi informazioni
            </a>
            <a
              href="#come-funziona"
              className="inline-flex items-center gap-2 text-base font-semibold text-[#385244] hover:text-[#C98E6E] transition-colors duration-300"
              data-testid="hero-secondary-cta"
            >
              Scopri come funziona <ArrowDown size={18} />
            </a>
            <a
              href="#video-pazienti"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#385244] text-[#385244] hover:bg-[#385244] hover:text-white text-base font-bold px-7 py-3.5 transition-colors duration-300 active:scale-95"
              data-testid="hero-video-cta"
            >
              <PlayCircle size={20} /> Guarda le storie dei pazienti
            </a>
          </motion.div>
          {review && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 max-w-xl bg-white/70 backdrop-blur-sm border border-border rounded-2xl px-6 py-5"
              data-testid="hero-rotating-review"
            >
              <AnimatePresence mode="wait">
                <motion.figure
                  key={reviewIdx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={14} className={n <= Math.round(review.rating) ? "text-[#FBBC05] fill-[#FBBC05]" : "text-[#D8DDD9]"} />
                    ))}
                  </span>
                  <blockquote className="mt-2 text-sm md:text-base leading-relaxed text-[#15201A]">
                    "{shorten(review.text)}"
                  </blockquote>
                  <figcaption className="mt-2 text-xs text-[#5C6B61]">
                    <strong className="text-[#385244]">{review.author}</strong> · {review.relative_time} · recensione verificata su Google
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </motion.div>
          )}
        </div>

        <div className="relative">
          <motion.div style={{ y: imgY }} className="relative">
            <img
              src="/hero-sorriso.webp"
              srcSet="/hero-sorriso-480.webp 480w, /hero-sorriso-768.webp 768w, /hero-sorriso.webp 1020w"
              sizes="(max-width: 768px) 100vw, 50vw"
              alt="Paziente sorridente dopo il trattamento di implantologia"
              width="800"
              height="900"
              fetchPriority="high"
              decoding="async"
              className="w-full max-h-[80vh] object-cover rounded-t-[160px] rounded-b-[40px]"
              data-testid="hero-image"
            />
          </motion.div>
          {google?.rating && (
            <motion.a
              href={google.google_maps_uri || "https://www.google.com/search?q=gruppo+stomatologico+italiano+recensioni"}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -top-5 right-2 md:-right-4 bg-white border border-border rounded-2xl px-5 py-3.5 flex items-center gap-3 hover:scale-[1.03] transition-transform duration-300"
              data-testid="hero-google-badge"
            >
              <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div>
                <span className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className={n <= Math.round(google.rating) ? "text-[#FBBC05] fill-[#FBBC05]" : "text-[#D8DDD9]"} />
                  ))}
                  <strong className="ml-1.5 text-[#15201A]">{google.rating.toFixed(1).replace(".", ",")}</strong>
                </span>
                <p className="text-xs text-[#5C6B61] mt-0.5">{google.review_count} recensioni su Google</p>
              </div>
            </motion.a>
          )}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-6 -left-4 md:-left-10 bg-white border border-border rounded-2xl px-6 py-4 flex items-center gap-4"
            data-testid="hero-trust-badge"
          >
            <ShieldCheck className="text-[#385244]" size={34} strokeWidth={1.6} />
            <div>
              <p className="font-serif-display text-xl text-[#15201A] leading-none">Garanzia economica 10 anni</p>
              <p className="text-sm text-[#5C6B61] mt-1">Su tutto il lavoro compresi gli impianti (per 10 anni non spenderai più nulla)</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
